import { useState, useCallback, useId, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building,
  Plus,
  Trash2,
  Check,
  ChevronRight,
  ChevronLeft,
  PartyPopper,
} from 'lucide-react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import {
  useCreateOnboardingProperty,
  useCreateOnboardingUnits,
  useConfigureOnboardingPricing,
  useConfigureOnboardingPolicies,
  useCompleteOnboarding,
  useSkipOnboarding,
} from '@/hooks/api/useOnboarding';
import type {
  OnboardingUnitItem,
  OnboardingPricingItem,
} from '@/services/api/onboarding.api';

// ─── Constants ────────────────────────────────────────────────────────────────

const STEP_LABELS = ['Property', 'Units', 'Pricing', 'Policies', 'Complete'] as const;

const PROPERTY_TYPES = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'serviced_apartment', label: 'Serviced Apartment' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'bnb', label: 'B&B' },
] as const;

const UNIT_TYPES = [
  { value: 'studio', label: 'Studio' },
  { value: '1bed', label: '1 Bedroom' },
  { value: '2bed', label: '2 Bedroom' },
  { value: '3bed', label: '3 Bedroom' },
  { value: 'penthouse', label: 'Penthouse' },
] as const;

const CANCELLATION_POLICIES = [
  {
    value: 'flexible',
    label: 'Flexible',
    description: 'Free cancellation up to 24 hours before check-in',
  },
  {
    value: 'moderate',
    label: 'Moderate',
    description: 'Free cancellation up to 5 days before check-in',
  },
  {
    value: 'strict',
    label: 'Strict',
    description: '50% refund up to 7 days before check-in',
  },
] as const;

function generateTimeOptions(): string[] {
  const times: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      times.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return times;
}

const TIME_OPTIONS = generateTimeOptions();

const DEFAULT_TIMEZONE = 'Asia/Kolkata';

// ─── Form State Types ─────────────────────────────────────────────────────────

interface PropertyFormState {
  name: string;
  address: string;
  city: string;
  country: string;
  description: string;
  propertyType: string;
  timezone: string;
  currency: string;
}

interface UnitFormItem {
  id: string;
  name: string;
  type: string;
  capacity: string;
  basePrice: string;
}

interface PricingFormItem {
  unitId: string;
  unitName: string;
  basePrice: string;
  weekendPrice: string;
  minimumStay: string;
}

interface PoliciesFormState {
  checkInTime: string;
  checkOutTime: string;
  cancellationPolicy: string;
  houseRules: string;
  petPolicy: string;
  smokingPolicy: string;
}

// ─── Validation ───────────────────────────────────────────────────────────────

type FieldErrors = Record<string, string>;

function validateProperty(form: PropertyFormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.name.trim()) errors['name'] = 'Property name is required.';
  if (!form.address.trim()) errors['address'] = 'Address is required.';
  if (!form.city.trim()) errors['city'] = 'City is required.';
  if (!form.country.trim()) errors['country'] = 'Country is required.';
  if (!form.propertyType) errors['propertyType'] = 'Property type is required.';
  return errors;
}

function validateUnits(units: UnitFormItem[]): FieldErrors {
  const errors: FieldErrors = {};
  if (units.length === 0) {
    errors['_general'] = 'At least one unit is required.';
    return errors;
  }
  units.forEach((unit, idx) => {
    if (!unit.name.trim()) errors[`unit_${idx}_name`] = 'Unit name is required.';
    if (!unit.type) errors[`unit_${idx}_type`] = 'Unit type is required.';
    const cap = parseInt(unit.capacity, 10);
    if (!unit.capacity || isNaN(cap) || cap < 1) {
      errors[`unit_${idx}_capacity`] = 'Capacity must be at least 1.';
    }
    const price = parseFloat(unit.basePrice);
    if (!unit.basePrice || isNaN(price) || price <= 0) {
      errors[`unit_${idx}_basePrice`] = 'Nightly rate must be greater than 0.';
    }
  });
  return errors;
}

function validatePolicies(form: PoliciesFormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.checkInTime) errors['checkInTime'] = 'Check-in time is required.';
  if (!form.checkOutTime) errors['checkOutTime'] = 'Check-out time is required.';
  if (!form.cancellationPolicy) errors['cancellationPolicy'] = 'Cancellation policy is required.';
  return errors;
}

// ─── Select Component ─────────────────────────────────────────────────────────

function Select({
  label,
  value,
  onChange,
  options,
  error,
  required,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">
        {label}
        {required && (
          <span className="ml-1 text-red-500" aria-label="required">
            *
          </span>
        )}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        aria-required={required}
        className={cn(
          'block w-full rounded-lg border px-3 py-2 text-sm text-gray-900',
          'transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
          'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500',
        )}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && (
        <p id={errorId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Textarea Component ───────────────────────────────────────────────────────

function Textarea({
  label,
  value,
  onChange,
  error,
  required,
  disabled,
  placeholder,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
}) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">
        {label}
        {required && (
          <span className="ml-1 text-red-500" aria-label="required">
            *
          </span>
        )}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        aria-required={required}
        placeholder={placeholder}
        className={cn(
          'block w-full rounded-lg border px-3 py-2 text-sm text-gray-900',
          'placeholder:text-gray-400',
          'transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
          'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500',
          'resize-none',
        )}
      />
      {error && (
        <p id={errorId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Toggle Component ─────────────────────────────────────────────────────────

function Toggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  const id = useId();

  return (
    <div className="flex items-start gap-3">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
          'transition-colors duration-200 ease-in-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'bg-primary-600' : 'bg-gray-200',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0',
            'transition duration-200 ease-in-out',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </button>
      <div className="flex-1">
        <label htmlFor={id} className="text-sm font-medium text-gray-700 cursor-pointer">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
}

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ currentStep }: { currentStep: number }) {
  return (
    <nav aria-label="Onboarding progress" className="w-full">
      <ol className="flex items-center justify-between">
        {STEP_LABELS.map((label, idx) => {
          const isCompleted = idx < currentStep;
          const isCurrent = idx === currentStep;
          const isFuture = idx > currentStep;

          return (
            <li
              key={label}
              className={cn(
                'flex flex-col items-center relative',
                idx < STEP_LABELS.length - 1 ? 'flex-1' : '',
              )}
            >
              <div className="flex items-center w-full">
                {/* Circle */}
                <div
                  aria-current={isCurrent ? 'step' : undefined}
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-full shrink-0 text-sm font-semibold transition-colors',
                    isCompleted && 'bg-green-500 text-white',
                    isCurrent && 'bg-primary-600 text-white ring-4 ring-primary-100',
                    isFuture && 'bg-white border-2 border-gray-300 text-gray-400',
                  )}
                >
                  {isCompleted ? (
                    <Check size={14} aria-hidden="true" />
                  ) : (
                    <span>{idx + 1}</span>
                  )}
                </div>

                {/* Connector line */}
                {idx < STEP_LABELS.length - 1 && (
                  <div
                    aria-hidden="true"
                    className={cn(
                      'flex-1 h-0.5 mx-2',
                      isCompleted ? 'bg-green-500' : 'bg-gray-200',
                    )}
                  />
                )}
              </div>

              {/* Label */}
              <span
                className={cn(
                  'mt-2 text-xs font-medium',
                  isCompleted && 'text-green-600',
                  isCurrent && 'text-primary-600',
                  isFuture && 'text-gray-400',
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ─── Step 1: Property Details ─────────────────────────────────────────────────

function PropertyStep({
  form,
  onChange,
  errors,
  disabled,
}: {
  form: PropertyFormState;
  onChange: (form: PropertyFormState) => void;
  errors: FieldErrors;
  disabled: boolean;
}) {
  function update(field: keyof PropertyFormState) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...form, [field]: e.target.value });
    };
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Property Details</h2>
        <p className="text-sm text-gray-500 mt-1">
          Tell us about your first property. You can add more properties later.
        </p>
      </div>

      <Input
        label="Property name"
        type="text"
        value={form.name}
        onChange={update('name')}
        error={errors['name']}
        placeholder="e.g. Sunset Beach Apartments"
        required
        disabled={disabled}
      />

      <Select
        label="Property type"
        value={form.propertyType}
        onChange={(v) => onChange({ ...form, propertyType: v })}
        options={[...PROPERTY_TYPES]}
        error={errors['propertyType']}
        placeholder="Select property type"
        required
        disabled={disabled}
      />

      <Input
        label="Address"
        type="text"
        value={form.address}
        onChange={update('address')}
        error={errors['address']}
        placeholder="123 Main Street"
        required
        disabled={disabled}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="City"
          type="text"
          value={form.city}
          onChange={update('city')}
          error={errors['city']}
          placeholder="London"
          required
          disabled={disabled}
        />
        <Input
          label="Country"
          type="text"
          value={form.country}
          onChange={update('country')}
          error={errors['country']}
          placeholder="United Kingdom"
          required
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select
          label="Timezone"
          value={form.timezone}
          onChange={(v) => onChange({ ...form, timezone: v })}
          disabled={disabled}
          options={[
            { value: 'Asia/Kolkata',      label: 'Asia/Kolkata (IST, UTC+5:30)' },
            { value: 'Europe/London',     label: 'Europe/London (GMT/BST)' },
            { value: 'Europe/Paris',      label: 'Europe/Paris (CET, UTC+1)' },
            { value: 'Europe/Berlin',     label: 'Europe/Berlin (CET, UTC+1)' },
            { value: 'Europe/Madrid',     label: 'Europe/Madrid (CET, UTC+1)' },
            { value: 'Europe/Rome',       label: 'Europe/Rome (CET, UTC+1)' },
            { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET, UTC+1)' },
            { value: 'Europe/Moscow',     label: 'Europe/Moscow (MSK, UTC+3)' },
            { value: 'America/New_York',  label: 'America/New_York (EST/EDT)' },
            { value: 'America/Chicago',   label: 'America/Chicago (CST/CDT)' },
            { value: 'America/Denver',    label: 'America/Denver (MST/MDT)' },
            { value: 'America/Los_Angeles','label': 'America/Los_Angeles (PST/PDT)' },
            { value: 'America/Toronto',   label: 'America/Toronto (EST/EDT)' },
            { value: 'America/Vancouver', label: 'America/Vancouver (PST/PDT)' },
            { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT, UTC-3)' },
            { value: 'America/Dubai',     label: 'Asia/Dubai (GST, UTC+4)' },
            { value: 'Asia/Dubai',        label: 'Asia/Dubai (GST, UTC+4)' },
            { value: 'Asia/Singapore',    label: 'Asia/Singapore (SGT, UTC+8)' },
            { value: 'Asia/Tokyo',        label: 'Asia/Tokyo (JST, UTC+9)' },
            { value: 'Asia/Shanghai',     label: 'Asia/Shanghai (CST, UTC+8)' },
            { value: 'Asia/Bangkok',      label: 'Asia/Bangkok (ICT, UTC+7)' },
            { value: 'Asia/Karachi',      label: 'Asia/Karachi (PKT, UTC+5)' },
            { value: 'Asia/Dhaka',        label: 'Asia/Dhaka (BST, UTC+6)' },
            { value: 'Asia/Colombo',      label: 'Asia/Colombo (IST, UTC+5:30)' },
            { value: 'Asia/Kathmandu',    label: 'Asia/Kathmandu (NPT, UTC+5:45)' },
            { value: 'Australia/Sydney',  label: 'Australia/Sydney (AEST/AEDT)' },
            { value: 'Australia/Melbourne','label': 'Australia/Melbourne (AEST/AEDT)' },
            { value: 'Pacific/Auckland',  label: 'Pacific/Auckland (NZST/NZDT)' },
            { value: 'Africa/Nairobi',    label: 'Africa/Nairobi (EAT, UTC+3)' },
            { value: 'Africa/Cairo',      label: 'Africa/Cairo (EET, UTC+2)' },
            { value: 'Africa/Johannesburg','label': 'Africa/Johannesburg (SAST, UTC+2)' },
            { value: 'UTC',               label: 'UTC (Coordinated Universal Time)' },
          ]}
        />
        <Select
          label="Currency"
          value={form.currency}
          onChange={(v) => onChange({ ...form, currency: v })}
          options={[
            { value: 'GBP', label: 'GBP - British Pound' },
            { value: 'EUR', label: 'EUR - Euro' },
            { value: 'USD', label: 'USD - US Dollar' },
            { value: 'AED', label: 'AED - UAE Dirham' },
            { value: 'AUD', label: 'AUD - Australian Dollar' },
          ]}
          disabled={disabled}
        />
      </div>

      <Textarea
        label="Description"
        value={form.description}
        onChange={(v) => onChange({ ...form, description: v })}
        placeholder="A brief description of your property (optional)"
        disabled={disabled}
        rows={3}
      />
    </div>
  );
}

// ─── Step 2: Add Units ────────────────────────────────────────────────────────

let _unitCounter = 0;
function nextUnitId(): string {
  _unitCounter += 1;
  return `temp_unit_${_unitCounter}`;
}

function UnitsStep({
  units,
  onChange,
  errors,
  disabled,
}: {
  units: UnitFormItem[];
  onChange: (units: UnitFormItem[]) => void;
  errors: FieldErrors;
  disabled: boolean;
}) {
  function addUnit() {
    onChange([
      ...units,
      { id: nextUnitId(), name: '', type: '', capacity: '2', basePrice: '' },
    ]);
  }

  function removeUnit(idx: number) {
    if (units.length <= 1) return;
    onChange(units.filter((_, i) => i !== idx));
  }

  function updateUnit(idx: number, field: keyof UnitFormItem, value: string) {
    const updated = units.map((u, i) =>
      i === idx ? { ...u, [field]: value } : u,
    );
    onChange(updated);
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Add Units</h2>
        <p className="text-sm text-gray-500 mt-1">
          Add the rooms or units for your property. You need at least one unit.
        </p>
      </div>

      {errors['_general'] && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700"
        >
          {errors['_general']}
        </div>
      )}

      <div className="space-y-4">
        {units.map((unit, idx) => (
          <div
            key={unit.id}
            className="bg-gray-50 rounded-xl border border-gray-200 p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700">
                Unit {idx + 1}
              </span>
              {units.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeUnit(idx)}
                  disabled={disabled}
                  aria-label={`Remove unit ${idx + 1}`}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Unit name"
                type="text"
                value={unit.name}
                onChange={(e) => updateUnit(idx, 'name', e.target.value)}
                error={errors[`unit_${idx}_name`]}
                placeholder="e.g. Room 101"
                required
                disabled={disabled}
              />
              <Select
                label="Type"
                value={unit.type}
                onChange={(v) => updateUnit(idx, 'type', v)}
                options={[...UNIT_TYPES]}
                error={errors[`unit_${idx}_type`]}
                placeholder="Select type"
                required
                disabled={disabled}
              />
              <Input
                label="Max capacity"
                type="number"
                value={unit.capacity}
                onChange={(e) => updateUnit(idx, 'capacity', e.target.value)}
                error={errors[`unit_${idx}_capacity`]}
                placeholder="2"
                required
                disabled={disabled}
              />
              <Input
                label="Nightly rate"
                type="number"
                value={unit.basePrice}
                onChange={(e) => updateUnit(idx, 'basePrice', e.target.value)}
                error={errors[`unit_${idx}_basePrice`]}
                placeholder="100.00"
                required
                disabled={disabled}
              />
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addUnit}
        disabled={disabled}
        className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors disabled:opacity-50"
      >
        <Plus size={16} aria-hidden="true" />
        Add another unit
      </button>
    </div>
  );
}

// ─── Step 3: Pricing ──────────────────────────────────────────────────────────

function PricingStep({
  items,
  onChange,
  disabled,
}: {
  items: PricingFormItem[];
  onChange: (items: PricingFormItem[]) => void;
  disabled: boolean;
}) {
  function updateItem(idx: number, field: keyof PricingFormItem, value: string) {
    const updated = items.map((item, i) =>
      i === idx ? { ...item, [field]: value } : item,
    );
    onChange(updated);
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Configure Pricing</h2>
        <p className="text-sm text-gray-500 mt-1">
          Fine-tune pricing for each unit. Weekend prices and minimum stay are optional.
        </p>
      </div>

      <div className="space-y-4">
        {items.map((item, idx) => (
          <div
            key={item.unitId}
            className="bg-gray-50 rounded-xl border border-gray-200 p-4"
          >
            <p className="text-sm font-semibold text-gray-700 mb-3">
              {item.unitName}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                label="Base price / night"
                type="number"
                value={item.basePrice}
                onChange={(e) => updateItem(idx, 'basePrice', e.target.value)}
                placeholder="100.00"
                disabled={disabled}
              />
              <Input
                label="Weekend price / night"
                type="number"
                value={item.weekendPrice}
                onChange={(e) => updateItem(idx, 'weekendPrice', e.target.value)}
                placeholder="Optional"
                disabled={disabled}
              />
              <Input
                label="Minimum stay (nights)"
                type="number"
                value={item.minimumStay}
                onChange={(e) => updateItem(idx, 'minimumStay', e.target.value)}
                placeholder="1"
                disabled={disabled}
              />
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500">
          No units to configure. Go back and add units first.
        </div>
      )}
    </div>
  );
}

// ─── Step 4: Policies ─────────────────────────────────────────────────────────

function PoliciesStep({
  form,
  onChange,
  errors,
  disabled,
}: {
  form: PoliciesFormState;
  onChange: (form: PoliciesFormState) => void;
  errors: FieldErrors;
  disabled: boolean;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Property Policies</h2>
        <p className="text-sm text-gray-500 mt-1">
          Set default policies for your property. These can be adjusted later.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select
          label="Check-in time"
          value={form.checkInTime}
          onChange={(v) => onChange({ ...form, checkInTime: v })}
          options={TIME_OPTIONS.map((t) => ({ value: t, label: t }))}
          error={errors['checkInTime']}
          required
          disabled={disabled}
        />
        <Select
          label="Check-out time"
          value={form.checkOutTime}
          onChange={(v) => onChange({ ...form, checkOutTime: v })}
          options={TIME_OPTIONS.map((t) => ({ value: t, label: t }))}
          error={errors['checkOutTime']}
          required
          disabled={disabled}
        />
      </div>

      {/* Cancellation Policy */}
      <fieldset>
        <legend className="text-sm font-medium text-gray-700 mb-3">
          Cancellation policy <span className="text-red-500" aria-label="required">*</span>
        </legend>
        {errors['cancellationPolicy'] && (
          <p role="alert" className="text-xs text-red-600 mb-2">
            {errors['cancellationPolicy']}
          </p>
        )}
        <div className="space-y-2">
          {CANCELLATION_POLICIES.map((policy) => (
            <label
              key={policy.value}
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                form.cancellationPolicy === policy.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 bg-white hover:bg-gray-50',
                disabled ? 'cursor-not-allowed opacity-60' : '',
              )}
            >
              <input
                type="radio"
                name="cancellationPolicy"
                value={policy.value}
                checked={form.cancellationPolicy === policy.value}
                onChange={() => onChange({ ...form, cancellationPolicy: policy.value })}
                disabled={disabled}
                className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  {policy.label}
                </span>
                <p className="text-xs text-gray-500 mt-0.5">{policy.description}</p>
              </div>
            </label>
          ))}
        </div>
      </fieldset>

      <Textarea
        label="House rules"
        value={form.houseRules}
        onChange={(v) => onChange({ ...form, houseRules: v })}
        placeholder="e.g. No parties, quiet hours after 10 PM"
        disabled={disabled}
        rows={3}
      />

      <div className="space-y-4">
        <Toggle
          label="Pets allowed"
          description="Allow guests to bring pets to the property"
          checked={form.petPolicy === 'allowed'}
          onChange={(checked) =>
            onChange({ ...form, petPolicy: checked ? 'allowed' : 'not_allowed' })
          }
          disabled={disabled}
        />
        <Toggle
          label="Smoking allowed"
          description="Allow smoking on the property premises"
          checked={form.smokingPolicy === 'allowed'}
          onChange={(checked) =>
            onChange({ ...form, smokingPolicy: checked ? 'allowed' : 'not_allowed' })
          }
          disabled={disabled}
        />
      </div>
    </div>
  );
}

// ─── Step 5: Complete ─────────────────────────────────────────────────────────

function CompleteStep({
  propertyName,
  unitCount,
  onDashboard,
  disabled,
}: {
  propertyName: string;
  unitCount: number;
  onDashboard: () => void;
  disabled: boolean;
}) {
  return (
    <div className="text-center py-8 space-y-6">
      <div className="flex justify-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <PartyPopper size={28} className="text-green-600" aria-hidden="true" />
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          You&apos;re all set!
        </h2>
        <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
          Your property <strong className="text-gray-700">{propertyName}</strong> has
          been set up with{' '}
          <strong className="text-gray-700">
            {unitCount} unit{unitCount !== 1 ? 's' : ''}
          </strong>
          . You can continue setting up your account from the dashboard.
        </p>
      </div>

      <div className="flex flex-col items-center gap-3">
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={onDashboard}
          disabled={disabled}
        >
          Go to Dashboard
          <ChevronRight size={16} aria-hidden="true" />
        </Button>
        <p className="text-xs text-gray-400">
          You can always revisit these settings from the Settings page.
        </p>
      </div>
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export function OnboardingPage() {
  const navigate = useNavigate();

  // Step state (0-indexed: 0=Property, 1=Units, 2=Pricing, 3=Policies, 4=Complete)
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [apiError, setApiError] = useState('');

  // Data persisted across steps
  const [propertyId, setPropertyId] = useState('');
  const [createdUnitIds, setCreatedUnitIds] = useState<string[]>([]);

  // Form states
  const [propertyForm, setPropertyForm] = useState<PropertyFormState>({
    name: '',
    address: '',
    city: '',
    country: '',
    description: '',
    propertyType: '',
    timezone: DEFAULT_TIMEZONE,
    currency: 'USD',
  });

  const [unitForms, setUnitForms] = useState<UnitFormItem[]>([
    { id: nextUnitId(), name: '', type: '', capacity: '2', basePrice: '' },
  ]);

  const [pricingForms, setPricingForms] = useState<PricingFormItem[]>([]);

  const [policiesForm, setPoliciesForm] = useState<PoliciesFormState>({
    checkInTime: '15:00',
    checkOutTime: '11:00',
    cancellationPolicy: 'flexible',
    houseRules: '',
    petPolicy: 'not_allowed',
    smokingPolicy: 'not_allowed',
  });

  // Mutations
  const createPropertyMutation = useCreateOnboardingProperty();
  const createUnitsMutation = useCreateOnboardingUnits();
  const configurePricingMutation = useConfigureOnboardingPricing();
  const configurePoliciesMutation = useConfigureOnboardingPolicies();
  const completeMutation = useCompleteOnboarding();
  const skipMutation = useSkipOnboarding();

  const isSubmitting =
    createPropertyMutation.isPending ||
    createUnitsMutation.isPending ||
    configurePricingMutation.isPending ||
    configurePoliciesMutation.isPending ||
    completeMutation.isPending;

  // Derive pricing forms from unit forms when moving to pricing step
  const buildPricingForms = useCallback(
    (unitIds: string[]): PricingFormItem[] => {
      return unitForms.map((unit, idx) => ({
        unitId: unitIds[idx] ?? unit.id,
        unitName: unit.name || `Unit ${idx + 1}`,
        basePrice: unit.basePrice,
        weekendPrice: '',
        minimumStay: '1',
      }));
    },
    [unitForms],
  );

  function extractApiError(err: unknown): string {
    if (isAxiosError(err)) {
      const serverMessage = (
        err.response?.data as { message?: string } | undefined
      )?.message;
      if (serverMessage) return serverMessage;
      const status = err.response?.status;
      if (status != null && status >= 500) return 'Server error. Please try again later.';
    }
    return 'An unexpected error occurred. Please try again.';
  }

  // ── Navigation handlers ───────────────────────────────────────────────────

  async function handleNext() {
    setApiError('');
    setErrors({});

    if (step === 0) {
      const validationErrors = validateProperty(propertyForm);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      try {
        const result = await createPropertyMutation.mutateAsync({
          name: propertyForm.name.trim(),
          address: propertyForm.address.trim(),
          city: propertyForm.city.trim(),
          country: propertyForm.country.trim(),
          propertyType: propertyForm.propertyType,
          description: propertyForm.description.trim() || undefined,
          timezone: propertyForm.timezone.trim() || undefined,
          currency: propertyForm.currency || undefined,
        });
        const newPropertyId = (result as { id?: string })?.id ?? '';
        setPropertyId(newPropertyId);
        setStep(1);
      } catch (err) {
        setApiError(extractApiError(err));
      }
      return;
    }

    if (step === 1) {
      const validationErrors = validateUnits(unitForms);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      const unitsPayload: OnboardingUnitItem[] = unitForms.map((u) => ({
        name: u.name.trim(),
        type: u.type,
        capacity: parseInt(u.capacity, 10),
        basePrice: parseFloat(u.basePrice),
      }));

      try {
        const result = await createUnitsMutation.mutateAsync({
          propertyId,
          units: unitsPayload,
        });
        // Backend returns Unit[] — extract real UUIDs
        const units = result as Array<{ id: string }>;
        const unitIds: string[] = units.map((u) => u.id);
        setCreatedUnitIds(unitIds);
        setPricingForms(buildPricingForms(unitIds));
        setStep(2);
      } catch (err) {
        setApiError(extractApiError(err));
      }
      return;
    }

    if (step === 2) {
      const pricingPayload: OnboardingPricingItem[] = pricingForms.map(
        (item, idx) => {
          const weekendPrice = parseFloat(item.weekendPrice);
          const minimumStay = parseInt(item.minimumStay, 10);
          return {
            unitId: createdUnitIds[idx] ?? item.unitId,
            basePrice: parseFloat(item.basePrice) || 0,
            weekendPrice: isNaN(weekendPrice) ? undefined : weekendPrice,
            minimumStay: isNaN(minimumStay) ? undefined : minimumStay,
          };
        },
      );

      try {
        await configurePricingMutation.mutateAsync({ units: pricingPayload });
        setStep(3);
      } catch (err) {
        setApiError(extractApiError(err));
      }
      return;
    }

    if (step === 3) {
      const validationErrors = validatePolicies(policiesForm);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      try {
        await configurePoliciesMutation.mutateAsync({
          propertyId,
          checkInTime: policiesForm.checkInTime,
          checkOutTime: policiesForm.checkOutTime,
          cancellationPolicy: policiesForm.cancellationPolicy,
          houseRules: policiesForm.houseRules.trim() || undefined,
          petPolicy: policiesForm.petPolicy || undefined,
          smokingPolicy: policiesForm.smokingPolicy || undefined,
        });
        await completeMutation.mutateAsync();
        setStep(4);
      } catch (err) {
        setApiError(extractApiError(err));
      }
    }
  }

  function handleBack() {
    setErrors({});
    setApiError('');
    if (step > 0 && step < 4) {
      setStep(step - 1);
    }
  }

  async function handleSkip() {
    try {
      await skipMutation.mutateAsync();
      navigate('/dashboard', { replace: true });
    } catch {
      navigate('/dashboard', { replace: true });
    }
  }

  function handleGoToDashboard() {
    navigate('/dashboard', { replace: true });
  }

  // Memoize unit count for summary
  const unitCount = useMemo(() => unitForms.length, [unitForms]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header bar */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sm:px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
              <Building size={16} className="text-white" aria-hidden="true" />
            </div>
            <span className="text-base font-semibold text-gray-900">
              BestAcco PMS
            </span>
          </div>
          {step < 4 && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={skipMutation.isPending}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
            >
              {skipMutation.isPending ? 'Skipping...' : 'Skip Setup'}
            </button>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-4 py-8 sm:px-6">
        {/* Step Indicator */}
        <div className="mb-8">
          <StepIndicator currentStep={step} />
        </div>

        {/* API error banner */}
        {apiError && (
          <div
            role="alert"
            aria-live="assertive"
            className="mb-6 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
          >
            {apiError}
          </div>
        )}

        {/* Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8">
          {/* Step content */}
          {step === 0 && (
            <PropertyStep
              form={propertyForm}
              onChange={setPropertyForm}
              errors={errors}
              disabled={isSubmitting}
            />
          )}
          {step === 1 && (
            <UnitsStep
              units={unitForms}
              onChange={setUnitForms}
              errors={errors}
              disabled={isSubmitting}
            />
          )}
          {step === 2 && (
            <PricingStep
              items={pricingForms}
              onChange={setPricingForms}
              disabled={isSubmitting}
            />
          )}
          {step === 3 && (
            <PoliciesStep
              form={policiesForm}
              onChange={setPoliciesForm}
              errors={errors}
              disabled={isSubmitting}
            />
          )}
          {step === 4 && (
            <CompleteStep
              propertyName={propertyForm.name}
              unitCount={unitCount}
              onDashboard={handleGoToDashboard}
              disabled={false}
            />
          )}

          {/* Navigation buttons */}
          {step < 4 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              <div>
                {step > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleBack}
                    disabled={isSubmitting}
                  >
                    <ChevronLeft size={16} aria-hidden="true" />
                    Back
                  </Button>
                )}
              </div>
              <Button
                type="button"
                variant="primary"
                onClick={handleNext}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                {step === 3 ? 'Finish Setup' : 'Next'}
                {!isSubmitting && step < 3 && (
                  <ChevronRight size={16} aria-hidden="true" />
                )}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
